/**
 * datasource 命令：数据源探查。
 *   load-datasource-defs   加载指定服务器数据源的完整定义（确认 kind:server 的 id/type）
 *   load-all-datasource-defs 加载租户下全部已配置数据源定义（定位目标库，无需先知道 id；--list 只出精简摘要，大库省 token）
 *   list-datasets          列出某数据源下的表/视图
 *   build-database-tables  列举某数据源下的表/视图（贴近设计器，表单参数）
 *   build-fields           按 SQL 构建字段（含字段数组与样例数据）
 *   schema                 一次性导出全部表与列（写 SQL 前必做）
 *   verify-sql             按真实表结构确认单条 SQL
 *   list-fields            列已登记数据集字段（需整型 datasetId）
 *   sample-data            抽取已登记数据集样例数据（需整型 datasetId）
 *
 * 开发期优先用 load-datasource-defs 确认服务器数据源定义，
 * 再用 schema 一次性拿到全部表结构（写 SQL 的依据），
 * 最后用服务端数据源 ID 写报表（kind: server）。
 */

import { Command } from 'commander';
import { login, resolveTenantOrSelect } from '../client/session.js';
import { requireAuth, resolveAuth, resolveDefaultDatasourceId, saveDefaultDatasourceId, applyConfigDir } from '../core/config.js';
import {
  listDatasets,
  loadBeanDatasets,
  loadBeanFields,
  loadDatasourceDefs,
  loadAllDatasourceDefs,
  buildDatabaseTables,
  buildFields,
  buildSchema,
  listFields,
  sampleData,
  loadFunctions,
} from '../client/datasourceApi.js';
import { buildPromptSchema } from '../core/validate/buildPromptSchema.js';

export function registerDatasource(program: Command) {
  const ds = program.command('datasource').description('数据源与数据集探查');

  /** 解析数据源 ID：优先用显式传入的，否则回退到 load-all-datasource-defs 记录的默认上下文。 */
  const resolveDatasourceId = (flagId: string | undefined): string => {
    const id = flagId && String(flagId).trim() ? String(flagId).trim() : resolveDefaultDatasourceId();
    if (!id) {
      throw new Error(
        '缺少数据源 ID：请通过 --datasource-id 显式指定，或先执行 `datasource load-all-datasource-defs --tenantId <tenantId>`' +
          '（仅一个数据源时会自动记为默认上下文，后续可省略 --datasource-id）。',
      );
    }
    return id;
  };

  const auth = (cmd: Command) =>
    cmd
      .option('--base-url <url>', '服务端地址（缺省读 BASK_BASE_URL / .env）')
      .option('--username <user>', '登录用户名（Session 登录；与 --access-token 互斥，缺省读 BASK_USERNAME / .env）')
      .option('--password <pwd>', '登录密码（Session 登录；与 --access-token 互斥，缺省读 BASK_PASSWORD / .env）')
      .option('--access-token <token>', 'OAuth2 访问令牌（与 username/password 互斥；缺省读 BASK_ACCESS_TOKEN / .env）')
      .option('--access-token-header <name>', 'OAuth2 token 注入的 header 名，缺省 authorization（值形如 Bearer <token>）；缺省读 BASK_ACCESS_TOKEN_HEADER / .env')
      .option('--config-dir <dir>', '配置查找目录（含 .env / baskreport.config.json）')
      .option('--tenantId <id>', '租户 ID（可省略；省略时登录后自动解析唯一租户，多租户需显式指定）');

  /** 解析并校验鉴权参数（命令行 > 环境变量/.env > 配置），登录后自动解析 tenantId。 */
  const loginResolved = async (opts: any, ctx: string) => {
    applyConfigDir(opts);
    const resolved = resolveAuth(opts);
    requireAuth(resolved, ctx);
    const s = await login(resolved);
    // tenantId 未显式提供时，登录后调用 user/get 自动解析；多租户则引导用户（LLM 呈现候选 / TTY 交互）选择。
    s.tenantId = await resolveTenantOrSelect(s, resolved.tenantId);
    return s;
  };

  auth(
    ds
      .command('load-datasource-defs')
      .description('加载指定服务器数据源的完整定义（确认 kind:server 的 id/type）')
      .option('--datasource-id <id>', '数据源 ID（省略时回退到默认上下文 datasourceId）'),
  ).action(async (opts) => {
    const s = await loginResolved(opts, 'datasource 命令');
    const dsId = resolveDatasourceId(opts.datasourceId);
    const def = await loadDatasourceDefs(s, dsId, {
      tenantId: opts.tenantId,
    });
    console.log(JSON.stringify(def, null, 2));
  });

  auth(
    ds
      .command('load-all-datasource-defs')
      .description('加载租户下全部已配置服务器数据源的定义（写报表前定位目标库用，无需先知道 id）')
      .option('--list', '只输出精简摘要（每行 id/名称/类型，对齐排列），大库省 token；完整定义用 load-datasource-defs --datasource-id <id> 单独加载'),
  ).action(async (opts) => {
    const s = await loginResolved(opts, 'datasource 命令');
    const defs = await loadAllDatasourceDefs(s, {
      tenantId: opts.tenantId,
    });
    if (opts.list) {
      // 摘要模式：只给 id/name/type 三列（对齐排列），不输出 url/driver 等长字段。
      // 大库下定义体可能含超长 url 与完整配置，摘要能把输出压到每源一行。
      const rows = defs.map((d) => ({
        id: d.id != null ? String(d.id) : '?',
        name: d.name ?? '(未命名)',
        type: d.type ?? d.platform ?? '?',
      }));
      const wId = Math.max(...rows.map((r) => r.id.length));
      const wName = Math.max(...rows.map((r) => r.name.length));
      for (const r of rows) {
        console.log(`${r.id.padEnd(wId)}  ${r.name.padEnd(wName)}  ${r.type}`);
      }
      console.error(`\n共 ${defs.length} 个数据源。查看某数据源完整定义（含 driver/url）：datasource load-datasource-defs --datasource-id <id> --tenantId <t>`);
    } else {
      console.log(JSON.stringify(defs, null, 2));
    }
    // 仅一个数据源时，记为默认上下文：写入 baskreport.config.json 的 datasourceId，
    // 后续命令省略 --datasource-id 即回退到此默认数据源，无需重复判断/向用户确认。
    if (defs.length === 1) {
      const id = defs[0]?.id;
      if (id != null) {
        saveDefaultDatasourceId(String(id));
        console.error(`\n检测到唯一数据源，已记为默认上下文（datasourceId=${id}）。后续命令可省略 --datasource-id。`);
      }
    }
  });

  auth(
    ds
      .command('list-datasets')
      .description('列出某数据源下的表/视图')
      .option('--datasource-id <id>', '数据源 ID（省略时回退到默认上下文 datasourceId）'),
  ).action(async (opts) => {
    const s = await loginResolved(opts, 'datasource 命令');
    const dsId = resolveDatasourceId(opts.datasourceId);
    const list = await listDatasets(s, dsId);
    console.log(JSON.stringify(list, null, 2));
  });

  // BeanDataset 没有数据源（无 datasource-id），直接通过专用接口拉取数据集名称列表。
  auth(
    ds
      .command('list-bean-datasets')
      .description('列出 BeanDataset 数据集名称（BeanDataset 无数据源，无需 --datasource-id）')
      .option('--online', '在线模式（缺省 offline，即 online=false）')
      .option('--json', '仅输出 JSON（缺省额外打印紧凑列表）'),
  ).action(async (opts) => {
    const s = await loginResolved(opts, 'datasource 命令');
    const list = await loadBeanDatasets(s, {
      online: opts.online === true,
      tenantId: opts.tenantId,
    });
    console.log(JSON.stringify(list, null, 2));
    if (!opts.json) {
      // 带序号输出：AI 记下目标名称的序号，再用 list-bean-fields --index <n> 加载字段（全 ASCII，规避中文乱码）。
      list.forEach((name, i) => console.log(`${i + 1}. ${name}`));
      console.log(`\n共 ${list.length} 个 BeanDataset 数据集。用 \`datasource list-bean-fields --index <序号> --tenantId <t>\` 加载某数据集的字段（序号从源头实时取，无需传中文名）。` +
        `BeanDataset 由后端 bean 提供数据，无需选数据源、无需探表结构、无需写 SQL；据其名称在报表中声明 type:"bean" 的数据集并显式给出 fields 即可。`);
    }
  });

  // BeanDataset 字段按数据集名称加载，无整型 datasetId，故不能走 list-fields / build-fields。
  // 名称只走 --index：CLI 从源头 loadDatasets 实时取名称，命令里只出现 ASCII 序号（中文不进命令通道）。
  auth(
    ds
      .command('list-bean-fields')
      .description('列出 BeanDataset 的字段（按 list-bean-datasets 输出的序号，无需 --datasource-id / --dataset-id）')
      .requiredOption('--index <n>', 'list-bean-datasets 输出的数据集序号（1-based；CLI 从源头 loadDatasets 实时拉取名称列表后按序号取名称，全 ASCII 规避中文乱码）')
      .option('--online', '在线模式（缺省 offline，即 online=false）')
      .option('--json', '仅输出 JSON（缺省额外打印紧凑列表）'),
  ).action(async (opts) => {
    const s = await loginResolved(opts, 'datasource 命令');
    const idx = Number(opts.index);
    if (!Number.isInteger(idx) || idx < 1) throw new Error(`--index 必须是 >=1 的整数，收到：${opts.index}`);
    const list = await loadBeanDatasets(s, {
      online: opts.online === true,
      tenantId: opts.tenantId,
    });
    const target = list[idx - 1];
    if (!target) {
      const shown = list.slice(0, 10).map((n, i) => `  ${i + 1}. ${n}`).join('\n');
      throw new Error(
        `--index ${idx} 越界：当前共 ${list.length} 个 BeanDataset。候选：\n${shown}\n` +
          `请先执行 \`datasource list-bean-datasets --tenantId ${opts.tenantId ?? '<t>'}\` 查看最新列表后再指定。`,
      );
    }
    const datasetName = target;
    // 回显选中名称，便于与列表对照校对（走 stderr，不污染 stdout JSON）。
    console.error(`已选中数据集（序号 ${idx}）：${datasetName}`);
    const fields = await loadBeanFields(s, datasetName, {
      online: opts.online === true,
      tenantId: opts.tenantId,
    });
    console.log(JSON.stringify(fields, null, 2));
    if (!opts.json) {
      for (const f of fields) console.log(`${f.name}:${f.type ?? '?'}${f.label ? ` (${f.label})` : ''}`);
      console.log(`\n共 ${fields.length} 个字段。BeanDataset 字段需在报表 XML/IR 中显式声明，例如：<dataset type="bean" name="${datasetName}">` +
        fields.map((f) => `<field name="${f.name}" label="${f.label ?? f.name}" type="${f.type ?? 'string'}"/>`).join('') +
        `</dataset>`);
    }
  });

  auth(
    ds
      .command('build-database-tables')
      .description('列举某数据源下的表/视图（贴近设计器，表单参数）')
      .option('--datasource-id <id>', '数据源 ID（省略时回退到默认上下文 datasourceId）')
      .option('--datasource-type <type>', '数据源类型：jdbc（默认）或 api', 'jdbc'),
  ).action(async (opts) => {
    const s = await loginResolved(opts, 'datasource 命令');
    const dsId = resolveDatasourceId(opts.datasourceId);
    const tables = await buildDatabaseTables(s, dsId, {
      tenantId: opts.tenantId,
      datasourceType: opts.datasourceType,
    });
    console.log(JSON.stringify(tables, null, 2));
  });

  auth(
    ds
      .command('build-fields')
      .description('按 SQL 构建字段（含字段数组与样例数据）')
      .option('--datasource-id <id>', '数据源 ID（省略时回退到默认上下文 datasourceId）')
      .option('--datasource-type <type>', '数据源类型：jdbc（默认）或 api', 'jdbc')
      .option('--dataset-id <id>', 'API 数据源的数据集 ID（datasource-type=api 时必填）')
      .requiredOption('--sql <sql>', '查询 SQL，如 select * from dept（API 数据源可留空）'),
  ).action(async (opts) => {
    const s = await loginResolved(opts, 'datasource 命令');
    const dsId = resolveDatasourceId(opts.datasourceId);
    const { fields, rows } = await buildFields(s, dsId, opts.sql, {
      tenantId: opts.tenantId,
      datasourceType: opts.datasourceType,
      datasetId: opts.datasourceType === 'api' ? opts.datasetId : undefined,
    });
    console.log(JSON.stringify({ fields, rows }, null, 2));
  });

  // 写 SQL 之前的一次性 schema 探查：拿到全部表与列，避免凭空猜表名/列名。
  auth(
    ds
      .command('schema')
      .description('一次性导出数据源全部表与列（写 SQL 前必做，避免猜表名/列名）')
      .option('--datasource-id <id>', '数据源 ID（省略时回退到默认上下文 datasourceId）')
      .option(
        '--tables <names>',
        '仅探查指定表，逗号分隔；支持 * 通配前后缀（如 t_emp*）。缺省探查全部表',
      )
      .option('--list-tables', '只列出表名，不探查列（表很多时先用它定位目标表）')
      .option('--json', '输出 JSON（缺省输出紧凑文本，更省上下文）')
      .option('--concurrency <n>', '并发探查表数量，默认 5')
      .option('--datasource-type <type>', '数据源类型：jdbc（默认）或 api', 'jdbc'),
  ).action(async (opts) => {
    const s = await loginResolved(opts, 'datasource 命令');
    const optsDsId = resolveDatasourceId(opts.datasourceId);
    const tenantId = opts.tenantId;
    const datasourceType = opts.datasourceType ?? 'jdbc';

    // 只列表名：表多时先定位目标表，再用 --tables 精确探列，避免几百次探查。
    if (opts.listTables) {
      const all = await buildDatabaseTables(s, opts.datasourceId, {
        tenantId,
        datasourceType,
      });
      const names = all.map((t: any) => (typeof t === 'string' ? t : (t.name ?? t.tableName)));
      if (opts.json) {
        console.log(JSON.stringify(names, null, 2));
      } else {
        for (const n of names) console.log(n);
        console.log(`\n共 ${names.length} 张表。用 --tables 指定其中若干张再探查列。`);
      }
      return;
    }

    const tables = opts.tables
      ? String(opts.tables)
          .split(',')
          .map((t: string) => t.trim())
          .filter(Boolean)
      : undefined;
    const schema = await buildSchema(s, optsDsId, {
      tenantId,
      tables,
      datasourceType,
      concurrency: opts.concurrency ? Number(opts.concurrency) : undefined,
      // 表名写错是本次要解决的核心问题之一，必须显式报出并给出候选，
      // 而不是让它退化成一条含糊的「探查失败」。
      onUnknownTables: (unknown, allNames) => {
        console.error(`以下 ${unknown.length} 个表名在数据源中不存在：`);
        for (const u of unknown) {
          const b = u.toLowerCase();
          // 先按子串匹配；不中则剥掉 t_/tbl_ 之类前缀再按「共同词根」匹配，
          // 这样 t_emp 能联想到 employee，不至于给不出任何候选。
          const stem = b.replace(/^(t|tb|tbl|tab)[_-]?/, '');
          const near = allNames
            .filter((n) => {
              const a = n.toLowerCase();
              if (a.includes(b) || b.includes(a)) return true;
              return stem.length >= 3 && (a.includes(stem) || stem.includes(a));
            })
            .slice(0, 5);
          console.error(`  - ${u}${near.length ? `  → 你是不是想找：${near.join(', ')}` : ''}`);
        }
        console.error('提示：用 `datasource schema --datasource-id <id> --list-tables` 查看全部表名。\n');
      },
    });

    if (opts.json) {
      console.log(JSON.stringify(schema, null, 2));
      return;
    }
    // 紧凑文本：每表一行 `表名(列:类型, 列:类型)`，最省上下文。
    const failed: string[] = [];
    for (const t of schema) {
      if (t.error) {
        failed.push(`${t.name}: ${t.error}`);
        continue;
      }
      const cols = t.columns.map((c) => `${c.name}:${c.type ?? '?'}`).join(', ');
      console.log(`${t.name}(${cols})`);
    }
    console.log(`\n共 ${schema.length - failed.length} 张表探查成功。`);
    if (failed.length) {
      console.error(`以下 ${failed.length} 张表探查失败：`);
      for (const f of failed) console.error(`  - ${f}`);
    }
  });

  auth(
    ds
      .command('list-fields')
      .description('查询已登记数据集字段（需整型 datasetId）')
      .requiredOption('--dataset-id <id>', '数据集 ID'),
  ).action(async (opts) => {
    const s = await loginResolved(opts, 'datasource 命令');
    const list = await listFields(s, opts.datasetId);
    console.log(JSON.stringify(list, null, 2));
  });

  auth(
    ds
      .command('sample-data')
      .description('抽取已登记数据集样例数据（需整型 datasetId）')
      .requiredOption('--dataset-id <id>', '数据集 ID')
      .option('--param <k=v...>', '查询参数（可多次）', (v: string, prev: string[] = []) => [...prev, v], []),
  ).action(async (opts) => {
    const s = await loginResolved(opts, 'datasource 命令');
    const params: Record<string, string> = {};
    for (const p of opts.param ?? []) {
      const i = p.indexOf('=');
      if (i > 0) params[p.slice(0, i)] = p.slice(i + 1);
    }
    const data = await sampleData(s, opts.datasetId, params);
    console.log(JSON.stringify(data, null, 2));
  });

  // 表达式函数库：生成表达式前调用，拿到设计器真实可用的函数（名称/参数个数/语法），
  // 与 references/expression.md 配合，从源头避免臆造函数名或参数个数。
  auth(
    ds
      .command('load-functions')
      .description('加载表达式函数库（生成表达式前调用，拿到真实可用的函数名/参数个数/语法）')
      .option('--online', '在线模式（缺省 offline，即 online=false）')
      .option('--json', '仅输出 JSON（缺省额外打印紧凑列表）')
      .option('--category <name>', '仅显示指定分类（如 常用函数/报表函数/数学函数/日期和时间函数/文本函数/其它函数）')
      .option('--category-index <n>', '按分类序号过滤（先跑一次看序号清单；中文直传可能乱码时用序号最稳）'),
  ).action(async (opts) => {
    const s = await loginResolved(opts, 'datasource 命令');
    const funcs = await loadFunctions(s, {
      online: opts.online === true,
      tenantId: opts.tenantId,
    });
    if (opts.json) {
      console.log(JSON.stringify(funcs, null, 2));
      return;
    }
    // 紧凑列表：每行 `name(参数个数) [分类 | label]`，desc 去掉 <br>/转义换行压成单行。
    const clean = (d?: string) =>
      (d ?? '')
        .replace(/<br\s*\/?>/gi, ' ')
        .replace(/\\n/g, ' ')
        .replace(/<[^>]+>/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
    // 分类序号清单：中文直传可能乱码，优先用序号过滤（同 list-bean-fields --index 方案）。
    const cats = [...new Set(funcs.map((f) => f.category).filter(Boolean))].sort();
    const byIndex = opts.categoryIndex ? cats[Number(opts.categoryIndex) - 1] : undefined;
    const filtered = byIndex
      ? funcs.filter((f) => f.category === byIndex)
      : opts.category
        ? funcs.filter((f) => f.category === opts.category || f.category?.includes(opts.category))
        : funcs;
    for (const f of filtered) {
      const head = `${f.name}${f.parametersCount ? `(${f.parametersCount})` : ''}`;
      const meta = [f.category, f.label].filter(Boolean).join(' | ');
      console.log(`${head}${meta ? `  [${meta}]` : ''}`);
      const d = clean(f.desc);
      if (d) console.log(`    ${d}`);
    }
    console.log(`\n共 ${filtered.length} 个函数（全部 ${funcs.length} 个）。`);
    console.log('分类序号清单（--category-index <序号> 过滤，避免中文乱码）：');
    cats.forEach((c, i) => console.log(`  ${i + 1}. ${c}`));
    if (opts.category && filtered.length === 0) {
      console.log('提示：--category 未匹配到分类（PowerShell/自动化通道中文可能乱码），请用上方序号 --category-index 过滤。');
    }
    console.log('生成表达式前参考本函数库与 references/expression.md：只使用上表列出的函数名与参数个数，禁止臆造不存在的函数。');
  });

  // 写 SQL 之前，把真实 schema 整理成「可直接粘进 LLM prompt」的上下文块，
  // 从源头避免 LLM 凭空猜测表名/字段名。这是解决「首次生成 SQL 对不上数据库」的关键一步。
  auth(
    ds
      .command('schema-ctx')
      .description('导出可粘贴进 prompt 的真实表结构上下文（写 SQL 前必用，避免猜表名/列名）')
      .option('--datasource-id <id>', '数据源 ID（省略时回退到默认上下文 datasourceId）')
      .option('--tables <names>', '仅包含指定表，逗号分隔；支持 * 通配。缺省全部表')
      .option('--max-columns <n>', '单表超过该列数时提示按需裁剪，默认 40')
      .option('--datasource-type <type>', '数据源类型：jdbc（默认）或 api', 'jdbc'),
  ).action(async (opts) => {
    const s = await loginResolved(opts, 'datasource 命令');
    const dsId = resolveDatasourceId(opts.datasourceId);
    const tables = opts.tables
      ? String(opts.tables)
          .split(',')
          .map((t: string) => t.trim())
          .filter(Boolean)
      : undefined;
    const schema = await buildSchema(s, dsId, {
      tenantId: opts.tenantId,
      tables,
      datasourceType: opts.datasourceType,
    });
    const ctx = buildPromptSchema(schema, {
      maxColumnsPerTable: opts.maxColumns ? Number(opts.maxColumns) : undefined,
    });
    console.log(ctx);
  });

  // 按数据库真实表结构确认一条 SQL 的字段：探查实际返回列，并对声明字段做对比。
  auth(
    ds
      .command('verify-sql')
      .description('按数据库真实表结构确认 SQL：探查实际返回字段，并对比声明的字段名/类型')
      .option('--datasource-id <id>', '数据源 ID（省略时回退到默认上下文 datasourceId）')
      .requiredOption('--sql <sql>', '查询 SQL，如 select education, salary from t_emp')
      .option('--fields <json>', '声明的字段数组 JSON，如 [{"name":"education","type":"string"}]')
      .option('--strict-type', '类型不匹配升级为错误'),
  ).action(async (opts) => {
    const s = await loginResolved(opts, 'datasource 命令');
    const dsId = resolveDatasourceId(opts.datasourceId);
    const declared = (() => {
      if (!opts.fields) return [];
      try {
        const arr = JSON.parse(opts.fields);
        return Array.isArray(arr) ? arr : [];
      } catch {
        throw new Error('--fields 不是合法 JSON 数组');
      }
    })();

    const res = await buildFields(s, dsId, opts.sql, {
      tenantId: opts.tenantId,
      limit: 1,
    });
    const real = res.fields ?? [];
    const realNames = new Set(real.map((f) => (f.name ?? '').toLowerCase()));

    console.log('数据库实际返回字段：');
    console.log(JSON.stringify(real, null, 2));

    const problems: string[] = [];
    for (const f of declared) {
      if (!realNames.has(String(f.name).toLowerCase())) {
        problems.push(`声明字段「${f.name}」在 SQL 结果中不存在`);
      }
    }
    for (const rf of real) {
      if (!rf.name) continue;
      const d = declared.find((f) => String(f.name).toLowerCase() === rf.name.toLowerCase());
      if (!d) {
        problems.push(`SQL 返回字段「${rf.name}」未声明（建议补入）`);
        continue;
      }
      if (opts.strictType && d.type && rf.type && d.type !== rf.type) {
        problems.push(`字段「${rf.name}」类型不一致：声明 ${d.type}，实际 ${rf.type}`);
      }
    }
    if (problems.length) {
      console.error('校验问题：');
      for (const p of problems) console.error(`  - ${p}`);
      process.exitCode = 1;
    } else {
      console.log('校验通过：SQL 字段与声明一致。');
    }
  });
}
